import type { Position } from '@xyflow/react';

/**
 * High-level category classification for automation nodes.
 *
 * Categories are used to:
 * - Organize nodes in the palette by function
 * - Apply category-specific styling and icons
 * - Group related node types in the UI
 *
 * Category purposes:
 * - trigger: Defines when/how the automation starts (e.g., tag added, date-based)
 * - email: Email communication steps
 * - task: Action items and internal tasks for users
 * - action: General automation actions (tags, delays, conditionals, integrations)
 */
export type NodeCategory = 'trigger' | 'email' | 'task' | 'action';

/**
 * Base data structure for all nodes in the React Flow diagram.
 *
 * This interface provides the minimal common properties required by all node types,
 * whether they're automation steps, triggers, or other node types. Specific node
 * implementations (like AutomationStepNodeData) extend this interface with additional
 * properties.
 *
 * The Record<string, unknown> extension allows node-specific properties to be added
 * dynamically without breaking type safety.
 *
 * @example
 * ```typescript
 * const triggerNode: BaseNodeData = {
 *   label: 'Contact Added Tag: VIP',
 *   category: 'trigger',
 *   summary: 'Starts when VIP tag is added',
 *   metadata: { tagId: '123' }
 * };
 * ```
 */
export interface BaseNodeData extends Record<string, unknown> {
	/** Display name shown in the node UI */
	label: string;
	/** Optional brief description of the node's purpose or configuration */
	summary?: string;
	/** Key-value pairs for storing additional node-specific metadata */
	metadata?: Record<string, string>;
	/** High-level category for visual grouping and styling */
	category: NodeCategory;
	/** Array of output handle IDs for nodes with multiple outputs (e.g., switch nodes) */
	outputHandles?: string[];
	/** React Flow position for incoming edges (default: 'top') */
	targetPosition?: Position;
	/** React Flow position for outgoing edges (default: 'bottom') */
	sourcePosition?: Position;
}

/**
 * Constant mapping of all node type identifiers.
 *
 * These constants provide type-safe string literals for referencing node types
 * throughout the codebase. The 'as const' assertion creates a readonly object
 * with literal types for maximum type safety.
 *
 * Legacy types (TRIGGER, EMAIL, TASK) are from earlier implementation.
 * Current implementation uses the *_STEP variants with more granular types.
 */
export const NODE_TYPES = {
	TRIGGER: 'trigger',
	EMAIL_STEP: 'emailStep',
	TEXTING_STEP: 'textingStep',
	ACTION_ITEM_STEP: 'actionItemStep',
	ADD_TAG_STEP: 'addTagStep',
	REMOVE_TAG_STEP: 'removeTagStep',
	HANDWRITTEN_CARD_STEP: 'handwrittenCardStep',
	NO_ACTION_STEP: 'noActionStep',
	OPPORTUNITY_STEP: 'opportunityStep',
	HTML_NEWSLETTER_STEP: 'htmlNewsletterStep',
	SWITCH_STEP: 'switchStep',
	SEGMENT: 'segment',
	ERROR: 'error',
} as const;
